import { useState, useRef, useEffect } from 'react';
import Button from 'components/base/Button';
import { useNavigate } from 'react-router-dom';
import { Alert, Form, Row, Col, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import LoadingOverlay from 'components/loading-overlay/LoadingOverlay';
import { createUser, getUserGroups } from 'api/api';
import Select from 'react-select';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import makeAnimated from 'react-select/animated';

const breadcrumbItems = [
  {
    label: 'Users',
    url: '/users/users'
  },
  {
    label: 'Add',
    active: true
  }
];

const AddUser = () => {
  const navigate = useNavigate();
  const passwordInputRef = useRef(null);
  const animatedComponents = makeAnimated();
  const firstNameInputRef = useRef(null);
  const [error, setError] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userGroups, setUserGroup] = useState([]);
  const [userGroupId, setUserGroupId] = useState(null);

  useEffect(() => {
    if (firstNameInputRef.current) {
      firstNameInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    loadUserGroups();
  }, []);

  const loadUserGroups = () => {
    setError([]);
    setLoading(true);

    getUserGroups()
      .then(response => {
        setUserGroup(response.data.data);
      })
      .catch(err => {
        try {
          setError(err.response.data.errors);
        } catch (ex) {
          setError(['Please try again later...']);
        }
      });

    setTimeout(function () {
      setLoading(false);
    }, 1000);
  };

  const handleSubmit = e => {
    e.preventDefault();
    setPasswordsMatch(true);
    setError([]);

    if (password !== confirmPassword) {
      setPasswordsMatch(false);
      return;
    }

    setLoading(true);

    const formData = {
      name: name,
      email: email,
      password: password,
      user_group_id: userGroupId
    };

    console.log(formData);

    createUser(formData)
      .then(response => {
        if (response.status === 200) navigate('/users/users');
      })
      .catch(err => {
        try {
          setError(err.response.data.errors);
        } catch (error) {
          setError(['Please try again later...']);
        }
      });

    setTimeout(function () {
      setLoading(false);
    }, 1000);
  };

  const handlePasswordChange = e => {
    setPassword(e);
  };

  const handleConfirmPasswordChange = e => {
    setConfirmPassword(e);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const togglePasswordConfirmVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // const handleUserGroupChange = e => {
  //   setSystemAccess(false);
  //   setWeaponAccess(false);

  //   const userGroup = userGroups.find(g => g.id === e.value);

  //   if (userGroup !== undefined && userGroup !== null) {
  //     if (userGroup.system_access) setSystemAccess(true);

  //     if (userGroup.weapon_access) setWeaponAccess(true);

  //     if (userGroup.kiosk_admin_access) setKioskAdminAccess(true);
  //   }

  //   setUserGroupId(e.value);
  // };

  return (
    <div>
      <PageBreadcrumb items={breadcrumbItems} />
      <div className="mb-9">
        <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
          <div>
            <h2 className="mb-2">Add User</h2>
            <h5 className="text-700 fw-semi-bold">Add a new user</h5>
          </div>
        </div>
        <div>
          {Object.entries(error).length > 0 &&
            error.map((e, index) => (
              <Alert variant="soft-danger" key={index}>
                {e}
              </Alert>
            ))}
        </div>

        {loading && <LoadingOverlay />}

        <div>
          <Row className="justify-content-left">
            <Col md={8}>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Name*</Form.Label>
                      <Form.Control
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        ref={firstNameInputRef}
                        required
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email*</Form.Label>
                      <Form.Control
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>User Group*</Form.Label>
                      <Select
                        classNamePrefix="react-select"
                        className="mb-2"
                        closeMenuOnSelect={true}
                        components={animatedComponents}
                        options={
                          userGroups &&
                          userGroups.map(c => ({
                            value: c.id,
                            label: c.name
                          }))
                        }
                        onChange={e => setUserGroupId(e.value)}
                        required
                        name="user_group"
                        menuPlacement="auto"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Password*</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={e => handlePasswordChange(e.target.value)}
                          required
                          ref={passwordInputRef}
                        />
                        <InputGroup.Text
                          onClick={togglePasswordVisibility}
                          style={{ cursor: 'pointer' }}
                        >
                          <FontAwesomeIcon
                            icon={showPassword ? faEyeSlash : faEye}
                          />
                        </InputGroup.Text>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Confirm Password*</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={e =>
                            handleConfirmPasswordChange(e.target.value)
                          }
                          required
                        />
                        <InputGroup.Text
                          onClick={togglePasswordConfirmVisibility}
                          style={{ cursor: 'pointer' }}
                        >
                          <FontAwesomeIcon
                            icon={showConfirmPassword ? faEyeSlash : faEye}
                          />
                        </InputGroup.Text>
                      </InputGroup>
                      {!passwordsMatch && (
                        <Form.Text className="text-danger">
                          Passwords do not match.
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={2} className="d-flex align-items-end">
                    <Button variant="primary" type="submit" className="w-100">
                      Submit
                    </Button>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <Button
                      className="w-100"
                      variant="phoenix-secondary"
                      type="button"
                      onClick={() => navigate('/users/users')}
                    >
                      Discard
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default AddUser;
